import {
  MotionMasterMessage,
  ParameterAddressValue,
  ParameterTypeValue,
  ParameterTypeValueKey,
  ParameterValueType,
  ValueType,
} from './types';
import { differenceWith, inRange, intersectionWith } from 'lodash';
import { hexDecValueToNumber } from './util';
import { UnitConverterId } from './units';

/**
 * Defines visualization settings for a parameter when displayed on plots or charts.
 */
export type ParameterPlot = {
  /**
   * Axis association for plotting.
   *
   * - `'y1'` — Primary Y-axis
   * - `'y2'` — Secondary Y-axis
   */
  axis: 'y1' | 'y2';

  /**
   * Color code (typically in HEX or RGB) used to render this parameter on the plot.
   */
  color: string;

  /**
   * Determines whether the parameter should be visible on the plot.
   */
  visible: boolean;
};

export const parameterIdToPlotColor: { [k: string]: string } = {
  '0x20F0:00': '#FFFFFF', // Timestamp
  '0x6041:00': '#CCCCCC', // Statusword
  '0x6064:00': '#B82698', // Position actual value
  '0x607A:00': '#FF7F2A', // Target position
  '0x6062:00': '#FF7F2A', // Position demand value
  '0x60FC:00': '#FF7F2A', // Position demand internal value
  '0x606C:00': '#A2CD39', // Velocity actual value
  '0x60FF:00': '#FF5555', // Target velocity
  '0x6077:00': '#FFDD55', // Torque actual value
  '0x6071:00': '#C8BEB7', // Target torque
};

export const parameterIdToPlotAxis: { [k: string]: 'y1' | 'y2' } = {
  '0x20F0:00': 'y1', // Timestamp
  '0x6064:00': 'y1', // Position actual value
  '0x607A:00': 'y1', // Target position
  '0x6062:00': 'y1', // Position demand value
  '0x60FC:00': 'y1', // Position demand internal value
};

export const parameterIdToRadix: { [key: string]: 2 | 16 | 10 } = {
  '0x1000:00': 16,
  '0x2501:00': 2,
  '0x2502:00': 2,
  '0x2503:00': 2,
  '0x2504:00': 2,
  '0x2601:00': 2,
  '0x2602:00': 2,
  '0x2603:00': 2,
  '0x2604:00': 2,
  '0x2611:01': 2,
  '0x2611:02': 2,
  '0x603F:00': 16,
  '0x6041:00': 2,
  '0x60FD:00': 2,
  '0x6502:00': 2,
  '0x6621:01': 2,
  '0x6621:02': 2,
  '0x6621:03': 2,
  '0x6621:04': 2,
};

export const betaParameterIds = [
  '0x2003:06', // Motor specific settings: Difference of Ld and Lq inductance
  '0x2010:04', // Torque controller: Field weakening enabled
  '0x2010:05', // Torque controller: Field weakening percentage
  '0x2010:13', // Torque controller: Virtual Braking Chopper enabled
  '0x2010:14', // Torque controller: Virtual Braking Chopper gain
  '0x2010:15', // Torque controller: Virtual Braking Chopper voltage level
  '0x2010:16', // Torque controller: MTPA enabled
  '0x2010:17', // Torque controller: Virtual Braking Chopper notification level
];

/**
 * Represents the recommended types for a parameter input.
 */
export type ParameterInputType = 'number' | 'text' | 'range' | 'select' | 'ipv4';

/**
 * Extended information for a device parameter derived from the EtherCAT
 * Slave Information (ESI) file.
 *
 * This interface represents additional metadata and constraints
 * extracted from the ESI file, which enhances the base device parameter
 * description with validation, description, unit, mapping capabilities, and UI hints.
 */
export interface ParameterEsiExtension {
  /** Indicates if the parameter is mandatory (true) or optional (false/undefined). */
  mandatory?: boolean;

  /** Minimum allowed numeric value for the parameter, if applicable. */
  min?: number | null;

  /** Maximum allowed numeric value for the parameter, if applicable. */
  max?: number | null;

  /** Default numeric value for the parameter, if defined. */
  defaultData?: number;

  /** Unit symbol associated with the parameter (empty string if none). */
  unit?: string | null;

  /** Size of the parameter in bits. */
  bitSize?: number;

  /**
   * Maximum length allowed for STRING type parameters,
   * adjusted to accommodate firmware-specific constraints.
   */
  strlen?: number;

  /** Raw ESI data type string (e.g., "UINT16", "STRING(50)"). */
  esiType?: string;

  /** Human-readable description of the parameter or subitem. */
  description?: string;

  /** Description specifically for record data types, if applicable. */
  recordDescription?: string;

  /** Parsed options for enumerated parameter values, if available. */
  options?: Record<string, any>;

  /** Indicates if the parameter can be mapped as an RxPDO. */
  canBeMappedAsRxPdo?: boolean;

  /** Indicates if the parameter can be mapped as a TxPDO. */
  canBeMappedAsTxPdo?: boolean;

  /** Flag indicating if the parameter belongs to an SMM module. */
  isSmm?: boolean;

  /** Group or category name for the parameter. */
  group?: string | null;

  /** Optional type value key string, prioritizing typeValueKey over typeValue. */
  typeValueKey?: ParameterTypeValueKey;

  /** Optional type value string representing the parameter data type. */
  typeValue?: ParameterTypeValueKey;

  /**
   * Suggested HTML form input type for the parameter.
   */
  inputType?: ParameterInputType;

  /** Original unit value preserved for reference or restoration. */
  originalUnit?: string;

  /** Interface unit value preserved for reference or restoration. */
  interfaceUnit?: string;

  /** Original options preserved for reference or restoration. */
  originalOptions?: Record<string, any>;

  /**
   * Error when extending this parameter with ESI: unit, min/max, documentation and options.
   * Typically the error means there is no object in ESI that matches this parameter index and subindex.
   */
  esiExtensionError?: Error;
}

/**
 * Represents a generic parameter with required properties: index, subindex, name, value, typeValueKey.
 */
export interface Parameter
  extends MotionMasterMessage.Status.DeviceParameterInfo.IParameter,
    MotionMasterMessage.Status.DeviceParameterValues.IParameterValue,
    ParameterEsiExtension {
  /**
   * Uniquely identifies this parameter.
   * For example, if this is a device parameter, the parameter ID could be "0x1080:02.8602-01-0000755-2243".
   */
  id: string;

  /**
   * Index from the object dictionary or invented one for the virtual parameter.
   */
  index: number;

  /**
   * Subindex from the object dictionary or invented one for the virtual parameter.
   */
  subindex: number;

  /**
   * The 'name' property is mandatory.
   */
  name: string;

  /**
   * The 'value' property can be a string, number, or buffer.
   */
  value: ParameterValueType;

  /**
   * The 'typeValueKey' property represents the 'oneof type_value' from the proto file.
   * It is used when parameter values are set on the device.
   */
  typeValueKey?: ParameterTypeValueKey;

  /**
   * Textual representation of numerical value options for a parameter.
   */
  options?: { [key: string]: number };

  /**
   * Value of the parameter that is considered disabled.
   */
  disabledValue?: number;

  /**
   * Optional plot configuration used for visualization on charts.
   */
  plot?: ParameterPlot;

  /**
   * Sets a special unit type on this parameter that allows users to switch the unit.
   * Each unit type comes with multiple options and parser and formatter functions.
   * For example 'position' unit type has options 'inc', 'deg' and 'rad'.
   */
  unitConverterId?: UnitConverterId;

  /**
   * Radix: binary, decimal or hexadecimal.
   * Used for parseInt and input display format.
   */
  radix?: 2 | 16 | 10;

  /**
   * Is this parameter defined in SMM module.
   */
  isSmm?: boolean;

  /**
   * Indicates if this parameter is considered a beta feature,
   * which may be subject to change or removal in future releases.
   * This flag can be used to conditionally display or hide the parameter in user interfaces,
   * or to provide warnings about its stability.
   */
  isBeta?: boolean;

  /**
   * For parameters that correspond to motion master proto fields the fieldName should be
   * camelCase proto field name, otherwise the value is arbitrary.
   */
  fieldName?: string;
}

export type ParameterIndexSubindex = Pick<Parameter, 'index' | 'subindex'>;

/**
 * Make parameter id by providing a tuple of index and subindex.
 *
 * @param tuple - an array of index and subindex
 * @throws {Error} if index or subindex are less than 0
 * @returns combined index and subindex in uppercase hexadecimal format, e.g. "0x60FE:02"
 */
export function makeParameterId(tuple: [number | null | undefined, number | null | undefined]): string;

/**
 * Make parameter id by providing an object which has index and subindex properties.
 *
 * @param parameter - an object with optional index and subindex properties
 * @throws {Error} if index or subindex are less than 0
 * @returns combined index and subindex in uppercase hexadecimal format, e.g. "0x60FE:02"
 */
export function makeParameterId(parameter: { index?: number | null; subindex?: number | null }): string;

/**
 * Make parameter id by providing index and subindex.
 *
 * @param index - from 0x0000 to 0xFFFF for objects defined in ESI and >0xFFFF for custom object
 * @param subindex - defaults to 0 if not provided
 * @throws {Error} if index or subindex are less than 0
 * @returns combined index and subindex in uppercase hexadecimal format, e.g. "0x60FE:02"
 */
export function makeParameterId(index?: number | null, subindex?: number | null): string;

/**
 * Make parameter id by providing different arguments.
 *
 * The purpose of this declaration is to expose the signature of the implementation for {@link makeDeviceParameterId} function.
 *
 * @param a - number or an object with index and subindex or tuple of index and subindex
 * @param b - optional subindex, defaults to 0 if not provided
 * @throws {Error} if index or subindex are less than 0
 * @returns combined index and subindex in uppercase hexadecimal format, e.g. "0x60FE:02"
 */
export function makeParameterId(
  a?:
    | (number | null)
    | { index?: number | null; subindex?: number | null }
    | [number | null | undefined, number | null | undefined],
  b?: number | null
): string;

export function makeParameterId(
  a?:
    | (number | null)
    | { index?: number | null; subindex?: number | null }
    | [number | null | undefined, number | null | undefined],
  b?: number | null
): string {
  let index = 0;
  let subindex = 0;

  if (Array.isArray(a)) {
    if (a[0]) {
      index = a[0];
    }
    if (a[1]) {
      subindex = a[1];
    }
  } else if (typeof a === 'object') {
    if (a?.index) {
      index = a.index;
    }
    if (a?.subindex) {
      subindex = a.subindex;
    }
  } else {
    if (typeof a === 'number') {
      index = a;
    }
    if (typeof b === 'number') {
      subindex = b;
    }
  }

  if (index < 0) {
    throw new Error(`The provided index to makeParameterId must be greater than 0: ${index}`);
  }

  if (subindex < 0) {
    throw new Error(`The provided subindex to makeParameterId must be greater than 0: ${subindex}`);
  }

  const indexMaxLength = index > 0xffff ? 8 : 4;
  const x = index.toString(16).toUpperCase().padStart(indexMaxLength, '0');
  const y = subindex.toString(16).toUpperCase().padStart(2, '0');

  return `0x${x}:${y}`;
}

/**
 * Example of parameter id is "0x2110:1A" where:
 * - 0x2110 is object index in hexadecimal format
 * - 1A is subindex in hexadecimal format
 */
export const parameterIdRegExp = /^0x([0-9a-fA-F]{4,}):([0-9a-fA-F]{2})$/;

/**
 * Split device parameter id into parts.
 *
 * @param id - parameter id like "0x2110:1A"
 * @returns tuple of index and subindex
 */
export function splitParameterId(id: string): [number, number] {
  const match = id.match(parameterIdRegExp);

  if (!match) {
    throw new Error(`Parameter id "${id}" doesn't match the regular expression: ${parameterIdRegExp}`);
  }

  return [
    parseInt(match[1], 16), // index
    parseInt(match[2], 16), // subindex
  ];
}

/**
 * Check if the provided string matches the parameter ID.
 */
export function isParameterId(id: string): boolean {
  return parameterIdRegExp.test(id);
}

/**
 * Parameter comparison function.
 *
 * Typically provided to sorting functions. Compares parameters by index and subindex.
 */
export function parametersCompareFn(
  a: Pick<Parameter, 'index' | 'subindex'>,
  b: Pick<Parameter, 'index' | 'subindex'>
): number {
  if (a.index > b.index) {
    return 1;
  } else if (a.index < b.index) {
    return -1;
  } else {
    if (a.subindex > b.subindex) {
      return 1;
    } else if (a.subindex < b.subindex) {
      return -1;
    } else {
      return 0;
    }
  }
}

/**
 * Creates an array of parameters not included in the other given array. Compares by index and subindex.
 */
export function differenceParameters<
  T extends Pick<Parameter, 'index' | 'subindex'> = Parameter,
  S extends Pick<Parameter, 'index' | 'subindex'> = Parameter
>(p1: T[], p2: S[]): T[] {
  return differenceWith(p1, p2, (a, b) => a.index === b.index && a.subindex === b.subindex);
}

/**
 * Creates an array of unique parameters that are included in both given arrays. Compares by index and subindex.
 */
export function intersectionParameters<
  T extends Pick<Parameter, 'index' | 'subindex'> = Parameter,
  S extends Pick<Parameter, 'index' | 'subindex'> = Parameter
>(p1: T[], p2: S[]): T[] {
  return intersectionWith(p1, p2, (a, b) => a.index === b.index && a.subindex === b.subindex);
}

/**
 * Creates an array of unique parameters that are included in both given arrays, but only if the values of parameters differ.
 */
export function intersectionParametersWithDifferentValues<
  T extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter,
  S extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter
>(p1: T[], p2: S[], epsilon = 0): T[] {
  return intersectionWith(p1, p2, (a, b) => {
    if (a.index === b.index && a.subindex === b.subindex) {
      // Use an epsilon or a tolerance to account for the limitations of floating-point representation.
      if (epsilon && typeof a.value === 'number' && typeof b.value === 'number') {
        return Math.abs(a.value - b.value) > epsilon;
      } else if (Array.isArray(a.value) && Array.isArray(b.value)) {
        return JSON.stringify(a.value) !== JSON.stringify(b.value);
      } else {
        return a.value !== b.value;
      }
    }
    return false;
  });
}

/**
 * Represents the result of comparing parameter sets.
 *
 * The Differences tuple contains an array of parameters that exist in one array but not in the other array.
 * The Intersections tuple contains an array of parameters that exist in both sets.
 * The Comparisons tuple contains an array of parameters that exist in both sets, with the additional valueOther from the other set of the matching parameter.
 */
export interface ParametersComparison<
  T extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter,
  S extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter,
  V = T & { valueOther: ParameterValueType },
  U = S & { valueOther: ParameterValueType }
> {
  differences: [T[], S[]];
  intersections: [T[], S[]];
  comparisons: [V[], U[]];
}

/**
 * Compare parameters.
 *
 * Compare parameters by creating the parameters comparison result, which includes the differences, intersections, and comparisons {@link ParametersComparison}.
 */
export function compareParameters<
  T extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter,
  S extends Pick<Parameter, 'index' | 'subindex' | 'value'> = Parameter
>(p1: T[], p2: S[], epsilon = 0): ParametersComparison<T, S> {
  const d1 = differenceParameters(p1, p2);
  const d2 = differenceParameters(p2, p1);

  const i1 = intersectionParametersWithDifferentValues(p1, p2, epsilon);
  const i2 = intersectionParametersWithDifferentValues(p2, p1, epsilon);

  const c1 = i1.map((p, index) => ({ ...p, valueOther: i2[index].value }));
  const c2 = i2.map((p, index) => ({ ...p, valueOther: i1[index].value }));

  return {
    differences: [d1, d2],
    intersections: [i1, i2],
    comparisons: [c1, c2],
  };
}

export function getParameterTypeValueDefault(key: ParameterTypeValueKey) {
  if (key === 'floatValue' || key === 'intValue' || key === 'uintValue') {
    return 0;
  } else if (key === 'rawValue') {
    return Uint8Array.from([]);
  } else if (key === 'stringValue') {
    return '';
  } else {
    return 0;
  }
}

export function getParameterTypeValue(
  parameter: MotionMasterMessage.Status.DeviceParameterValues.IParameterValue
): ParameterTypeValueKey | undefined {
  return MotionMasterMessage.Status.DeviceParameterValues.ParameterValue.create(parameter).typeValue;
}

export function getParameterValue(
  parameter: MotionMasterMessage.Status.DeviceParameterValues.IParameterValue & { value?: ParameterValueType | null }
): ParameterValueType {
  const key = getParameterTypeValue(parameter);

  if ('value' in parameter) {
    return parameter['value'] ?? (key ? getParameterTypeValueDefault(key) : 0);
  }

  if (key && key in parameter) {
    return parameter[key] ?? getParameterTypeValueDefault(key);
  }

  return 0;
}

export function parseParameterValue(type: ParameterTypeValueKey, value: number | string): string | number | Uint8Array {
  if (type === 'floatValue') {
    return typeof value === 'number' ? value : parseFloat(value);
  } else if (type === 'intValue') {
    return typeof value === 'number' ? value : parseInt(value, 10);
  } else if (type === 'rawValue') {
    if (typeof value === 'number') {
      return Uint8Array.from([value]);
    } else {
      let bytes: number[] = [];
      if (value.startsWith('0x')) {
        bytes =
          value
            .slice(2)
            .match(/.{1,2}/g)
            ?.map((h) => parseInt(h, 16)) ?? [];
      } else {
        bytes = value
          .split(',')
          .map((c) => c.trim())
          .map((c) => parseInt(c, 10));
      }
      return Uint8Array.from(bytes);
    }
  } else if (type === 'stringValue') {
    return typeof value === 'string' ? value : value.toString();
  } else if (type === 'uintValue') {
    return typeof value === 'number' ? value : parseInt(value, 10);
  } else {
    throw new Error(`Unrecognized ParameterTypeValueKey: ${type}`);
  }
}

export function mapParameterTypeValueToObject(type: ParameterTypeValueKey, value: number | string): ParameterTypeValue {
  const p: ParameterTypeValue = {};
  if (type === 'floatValue') {
    p['floatValue'] = typeof value === 'number' ? value : parseFloat(value);
  } else if (type === 'intValue') {
    p['intValue'] = typeof value === 'number' ? value : parseInt(value, 10);
  } else if (type === 'rawValue') {
    if (typeof value === 'number') {
      p['rawValue'] = Uint8Array.from([value]);
    } else {
      let bytes: number[] = [];
      if (value.startsWith('0x')) {
        bytes =
          value
            .slice(2)
            .match(/.{1,2}/g)
            ?.map((h) => parseInt(h, 16)) ?? [];
      } else {
        bytes = value
          .split(',')
          .map((c) => c.trim())
          .map((c) => parseInt(c, 10));
      }
      p['rawValue'] = Uint8Array.from(bytes);
    }
  } else if (type === 'stringValue') {
    p['stringValue'] = typeof value === 'string' ? value : value.toString();
  } else if (type === 'uintValue') {
    p['uintValue'] = typeof value === 'number' ? value : parseInt(value, 10);
  }
  return p;
}

/**
 * The safety object index range in the CiA 402 profile is 0x6600 - 0x67EF.
 * In the manufacturer-specific area, we mirror this range with 0x2600 - 0x27EF.
 * However, prior to SMM, custom objects were added with indexes starting at 0x27.
 * To avoid conflicts, we need to adjust the condition to include only object indexes up to 0x2700.
 */
export function isSmmParameterIndex(index: number): boolean {
  return (index >= 0x2600 && index <= 0x26ff) || (index >= 0x6600 && index <= 0x67ef);
}

export function isRxPdoIndex(index: number): boolean {
  return inRange(index, 0x1600, 0x1800);
}

export function isTxPdoIndex(index: number): boolean {
  return inRange(index, 0x1a00, 0x1c00);
}

export function isPdoIndex(index: number): boolean {
  return isRxPdoIndex(index) || isTxPdoIndex(index);
}

export function filterRxPdoParameters<T extends ParameterIndexSubindex>(
  parameters: T[],
  excludeSubIndex000 = false
): T[] {
  return parameters.filter((p) => isRxPdoIndex(p.index) && (excludeSubIndex000 ? p.subindex > 0 : true)); // RxPDOs: Index area 0x1600 to 0x17FF
}

export function filterTxPdoParameters<T extends ParameterIndexSubindex>(
  parameters: T[],
  excludeSubIndex000 = false
): T[] {
  return parameters.filter((p) => isTxPdoIndex(p.index) && (excludeSubIndex000 ? p.subindex > 0 : true)); // TxPDOs: Index area 0x1A00 to 0x1BFF
}

/**
 * Fills in missing PDO mapping entries for each parameter group that starts with a subindex of 0.
 *
 * For each group of parameters with the same `index` and starting at `subindex` 0, this function:
 * 1. Adds the existing parameters to the output.
 * 2. Appends placeholder `Parameter` entries with incremented subindexes until the total count reaches `entriesPerPdo`.
 *
 * This is useful for ensuring that each PDO mapping table has a consistent number of entries,
 * even if some are unused and need to be filled with default placeholder values.
 *
 * @param parameters - The list of existing parameters to process.
 * @param entriesPerPdo - The number of total entries (including placeholders) each PDO should have. Defaults to 10.
 *
 * @returns A new array of `Parameter` objects where each PDO group is filled with entries up to `entriesPerPdo`.
 *
 * @example
 * ```ts
 * const params: Parameter[] = [
 *   { index: 0x1600, subindex: 0, ... },
 *   { index: 0x1600, subindex: 1, ... },
 * ];
 *
 * const filled = fillPdoParametersWithMappingEntries(params, 3);
 * // filled will include the two original entries plus one default placeholder with subindex 2
 * ```
 */
export function fillPdoParametersWithMappingEntries(parameters: Parameter[], entriesPerPdo = 10): Parameter[] {
  const output: Parameter[] = [];
  let i = 0;
  while (i < parameters.length) {
    if (parameters[i].subindex === 0) {
      let j = i;
      while (parameters[j] && parameters[j].index === parameters[i].index) {
        output.push(parameters[j]);
        j++;
      }
      let d = j - i;
      while (d <= entriesPerPdo) {
        output.push({
          id: parameters[i].id,
          index: parameters[i].index,
          subindex: d,
          name: parameters[i].name,
          uintValue: 0,
          value: 0,
          valueType: ValueType.UNSIGNED32,
          group: parameters[i].group,
          typeValueKey: 'uintValue',
          readAccess: true,
          writeAccess: true,
        });
        d++;
      }
      i = j;
    } else {
      i++;
    }
  }
  return output;
}

export function mapParameterToPdoSubItemValue(parameter: Parameter): number {
  const { index, subindex } = parameter;
  if (parameter.valueType && parameter.valueType < ValueType.VISIBLE_STRING) {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);
    const bitSize = mapValueTypeToBitSize(parameter.valueType);
    view.setUint16(0, index);
    view.setUint8(2, subindex);
    view.setUint8(3, bitSize);
    return view.getUint32(0);
  }
  return 0;
}

export function mapValueTypeToBitSize(valueType: ValueType): number {
  switch (valueType) {
    case ValueType.UNSPECIFIED:
      return 0;
    case ValueType.BOOLEAN:
      return 1;
    case ValueType.INTEGER8:
    case ValueType.UNSIGNED8:
      return 8;
    case ValueType.INTEGER16:
    case ValueType.UNSIGNED16:
      return 16;
    case ValueType.INTEGER32:
    case ValueType.UNSIGNED32:
    case ValueType.REAL32:
      return 32;
  }
  throw new Error(
    `Non-numeric value type "${valueType}" (data types with variable length) cannot be mapped to bit size. You will have to get this info from ESI.`
  );
}

export const valueTypeToParameterTypeValueKeyMap = new Map<ValueType, keyof Required<ParameterTypeValue>>([
  [ValueType.BOOLEAN, 'uintValue'],
  [ValueType.INTEGER8, 'intValue'],
  [ValueType.INTEGER16, 'intValue'],
  [ValueType.INTEGER32, 'intValue'],
  [ValueType.UNSIGNED8, 'uintValue'],
  [ValueType.UNSIGNED16, 'uintValue'],
  [ValueType.UNSIGNED32, 'uintValue'],
  [ValueType.REAL32, 'floatValue'],
  [ValueType.VISIBLE_STRING, 'stringValue'],
  [ValueType.OCTET_STRING, 'rawValue'],
  [ValueType.UNICODE_STRING, 'stringValue'],
  [ValueType.TIME_OF_DAY, 'uintValue'],
]);

export function createPdoParameterValues(
  parameters: Array<{ index: number; subindex: number; typeValue?: ParameterTypeValueKey }>,
  values: Array<ParameterValueType>
) {
  const output = parameters.map((parameter, i, arr) => {
    const { index, subindex, typeValue } = parameter;
    let value = values[i];
    if (subindex === 0) {
      value = arr.reduce((n, p, j) => (p.index === index && p.subindex > 0 && values[j] ? n + 1 : n), 0);
    }
    return [index, subindex, value, typeValue];
  }) as ParameterAddressValue[];

  for (let i = 0; i < output.length - 1; i++) {
    const [, subindex1, value1] = output[i];
    if (subindex1 > 0 && value1 === 0) {
      let j = i + 1;
      let [, subindex2, value2] = output[j];
      while (subindex2 > subindex1 && output[j]) {
        if (typeof value2 === 'number' && value2 > 0) {
          output[i][2] = value2;
          output[j][2] = 0;
          break;
        }
        if (output[++j]) {
          [, subindex2, value2] = output[j];
        }
      }
    }
  }

  return output;
}

/**
 * Resolves the index and subindex for the given parameter.
 *
 * @param parameter - The parameter to resolve, which can be a `ParameterIndexSubindex` object, a string, or a tuple of two numbers.
 * @returns A tuple containing the index and subindex.
 */
export function resolveParameterIndexAndSubindex(
  parameter: ParameterIndexSubindex | string | [number, number]
): [number, number] {
  if (Array.isArray(parameter)) {
    return parameter;
  } else if (typeof parameter === 'string') {
    return splitParameterId(parameter);
  } else {
    const { index, subindex } = parameter;
    return [index, subindex];
  }
}

/**
 * Custom Parameter object for configuring UI features in the client library.
 */
export type UIFeaturesConfigParameter = Pick<Parameter, 'index' | 'subindex' | 'writeAccess'>;

/**
 * Represents the configuration options for UI applications using the client library.
 * Defines available features, displayed elements, and editable parameters within the UI.
 */
export interface UIFeaturesConfig {
  /**
   * A list of configuration parameters with restricted access.
   * Optional; defines parameters that are displayed but not editable.
   */
  parameters?: UIFeaturesConfigParameter[];

  /**
   * Specifies whether the encoder configuration is disabled.
   * Optional: If undefined, the feature is treated as enabled.
   * @deprecated Use `disabledEncoderConfigurationIndexes` instead.
   */
  encoderConfigurationDisabled?: boolean;

  /**
   * Specifies the indexes of encoder configuration parameters that are disabled.
   */
  disabledEncoderConfigurationIndexes?: number[];

  /**
   * Specifies the type of Setup Wizard to be used.
   * - `'actilink'`: Uses the ACTILINK-S Setup Wizard, which supports unit configuration, limits setup, and auto-tuning.
   * - `'default'`: Uses the default Setup Wizard, which supports motor, brake, and encoder configuration.
   */
  setupWizard: 'actilink' | 'default';
}

/**
 * This array contains the list of parameters that are disabled by setting their value to a specific value.
 */
export const disabledValueParameters: Pick<Parameter, 'index' | 'subindex' | 'disabledValue'>[] = [
  /**
   * Protection: Undervoltage setpoint.
   * Beside 0, negative values are also considered disabled. We could introduce a range check here.
   */
  { index: 0x2006, subindex: 1, disabledValue: 0 },

  /**
   * Protection: Overvoltage setpoint.
   */
  { index: 0x2006, subindex: 2, disabledValue: 2147483647 },

  /**
   * Protection: Overcurrent setpoint.
   */
  { index: 0x2006, subindex: 3, disabledValue: 2147483647 },

  /**
   * Torque window: Value.
   */
  { index: 0x2014, subindex: 1, disabledValue: 65535 },

  /**
   * Velocity feed forward: Gain.
   */
  { index: 0x2015, subindex: 1, disabledValue: 0 },

  /**
   * Velocity following error: Window.
   */
  { index: 0x2018, subindex: 1, disabledValue: 65535 },

  /**
   * Velocity feedback filter: Cutoff frequency.
   */
  { index: 0x2021, subindex: 2, disabledValue: 0 },

  /**
   * Position feedback filter: Cutoff frequency.
   */
  { index: 0x2022, subindex: 2, disabledValue: 0 },

  /**
   * Following error window.
   */
  { index: 0x6065, subindex: 0, disabledValue: 4294967295 },

  /**
   * Position window.
   */
  { index: 0x6067, subindex: 0, disabledValue: 4294967295 },

  /**
   * Velocity window.
   */
  { index: 0x606d, subindex: 0, disabledValue: 65535 },

  /**
   * Software position limit: Min position limit.
   */
  { index: 0x607d, subindex: 1, disabledValue: -2147483648 },

  /**
   * Software position limit: Max position limit.
   */
  { index: 0x607d, subindex: 2, disabledValue: 2147483647 },
];

/**
 * Determines if the value of the given parameter is considered disabled.
 *
 * For example, the "Velocity Following Error: Window" parameter is considered disabled when its value is 65535.
 *
 * @param parameter - The parameter to check, which can be an object with `index`, `subindex`, and `value` properties, or a tuple of three numbers.
 * @returns `true` if the parameter is disabled, `false` otherwise.
 */
export function isParameterValueDisabled<T extends Pick<Parameter, 'index' | 'subindex' | 'value'>>(
  parameter: T | [number, number, number]
): boolean {
  const [index, subindex, value] = Array.isArray(parameter)
    ? parameter
    : [parameter.index, parameter.subindex, parameter.value];
  return disabledValueParameters.some((p) => p.index === index && p.subindex === subindex && p.disabledValue === value);
}

export const parameterAddressToColor: { [addr: string]: string } = {
  '0x20F0:00': '#FFFFFF', // Timestamp
  '0x6041:00': '#CCCCCC', // Statusword
  '0x6064:00': '#B82698', // Position actual value
  '0x607A:00': '#FF7F2A', // Target position
  '0x6062:00': '#FF7F2A', // Position demand value
  '0x60FC:00': '#FF7F2A', // Position demand internal value
  '0x606C:00': '#A2CD39', // Velocity actual value
  '0x60FF:00': '#FF5555', // Target velocity
  '0x6077:00': '#FFDD55', // Torque actual value
  '0x6071:00': '#C8BEB7', // Target torque
  '0x60F40000:01': '#DC5361', // Position following error
  '0x60F40000:02': '#D19147', // Velocity following error
  '0x60F40000:03': '#E4CE86', // Torque following error
};

/**
 * Parameter type value keys that hold a numeric value.
 */
const numericParameterTypeValueKeys: ParameterTypeValueKey[] = ['intValue', 'uintValue', 'floatValue'];

/**
 * Builds a permissive ESI extension for a parameter that cannot be matched against the ESI file.
 *
 * A parameter present in the device object dictionary but missing from the ESI file has no object
 * definition to derive type, unit, constraint or option information from. Returning an empty
 * extension would leave `inputType` undefined and such a parameter could not be rendered as a form
 * input, so infer the input type from the parameter type and leave the value unconstrained.
 *
 * @param parameter - partial parameter providing the type used to infer the input type
 *
 * @returns A {@link ParameterEsiExtension} with an inferred `inputType` and no min/max constraints.
 */
export function getParameterEsiExtensionFallback(
  parameter: Pick<Parameter, 'typeValueKey' | 'valueType'> & { typeValue?: ParameterTypeValueKey }
): ParameterEsiExtension {
  // Prefer the type value already resolved on the parameter and fall back to the one mapped from
  // the value type reported by the device.
  const typeValueKey =
    parameter.typeValueKey ??
    parameter.typeValue ??
    (parameter.valueType != null ? valueTypeToParameterTypeValueKeyMap.get(parameter.valueType) : undefined);

  return {
    // Strings and raw values are edited as text, everything numeric as a number.
    inputType: typeValueKey && numericParameterTypeValueKeys.includes(typeValueKey) ? 'number' : 'text',
    mandatory: false,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
  };
}

/**
 * Retrieves extended EtherCAT Slave Information (ESI) metadata for a given device parameter.
 *
 * This function extracts and constructs additional descriptive and validation
 * information for a device parameter based on the ESI file data, including
 * constraints, default values, units, descriptions, mapping capabilities,
 * and input type hints.
 *
 * The returned object extends the base device parameter with ESI-specific
 * metadata that can be used for validation, UI generation, and device
 * communication mapping.
 *
 * @param parameter - Partial device parameter containing `index`, `subindex`, `typeValueKey`,
 *                    and optionally `typeValue`. Identifies the parameter to extend.
 * @param info - The EtherCAT master or module info object containing
 *               descriptions and device dictionaries as parsed from the ESI file.
 *               (Type from `ecatmod.EtherCATInfo` expected but typed as `any` here.)
 * @param productCode - The EtherCAT device product code used to locate the matching device
 *                      description within the ESI data.
 *                      This product code can typically be read from the device's object dictionary
 *                      at index `0x1018` (Identity Object), subindex `2`.
 *
 * @returns A {@link ParameterEsiExtension} object containing extended parameter metadata,
 *          such as mandatory flags, min/max constraints, default values,
 *          units, bit sizes, descriptions, options, PDO mapping flags, SMM module flag,
 *          group information, type keys, and UI input hints.
 *
 * @remarks
 * - Supports parameter metadata extraction from both the main device profile dictionary
 *   and sub-modules (e.g., for modular devices like SMM LW2).
 * - For STRING types, the maximum length is adjusted to accommodate firmware limitations.
 * - Descriptions and options are parsed and cleaned from JSON-like strings.
 * - Handles both variable data type objects and complex objects with sub-items.
 * - Logs a warning if no matching device is found for the given product code.
 * - Preserves original unit and options for reference or restoration.
 * - When the parameter cannot be matched against the ESI file, falls back to
 *   {@link getParameterEsiExtensionFallback} and sets `esiExtensionError` describing what is missing,
 *   so that the parameter remains renderable and the gap in the ESI file stays reportable.
 */
export function getParameterEsiExtension(
  parameter: Pick<Parameter, 'index' | 'subindex' | 'typeValueKey' | 'valueType'> & {
    typeValue?: ParameterTypeValueKey;
  },
  info: any,
  productCode: number
): ParameterEsiExtension {
  const out: any = {};

  // Find the EtherCAT device matching the given product code.
  const ecatDevice = info.descriptions?.devices?.device.find((d: any) => {
    return hexDecValueToNumber(d.type?.productCode) === productCode;
  });

  // Assumes the device has only one profile.
  // If the device has multiple profiles, this logic may need to be extended.
  let dictionary = ecatDevice?.profile[0]?.dictionary;

  // If no dictionary is found in the device:
  // - It may be located in a sub-module instead.
  // - The device entry may not exist at all for this product code.
  // - The same product code could potentially be shared among multiple devices.
  if (!dictionary) {
    console.warn('No matching device in ESI found by product code!', productCode);

    return {
      ...out,
      ...getParameterEsiExtensionFallback(parameter),
      esiExtensionError: new Error(`No device matching the product code ${productCode} found in ESI.`),
    };
  }

  // Attempt to find the object by index in the primary device dictionary.
  let object = dictionary.objects.findByIndex(parameter.index);

  // If not found, look into device modules (e.g., for modular devices like SMM LW2, shared object dictionary).
  if (!object) {
    const deviceModules = info.descriptions?.getDeviceModules(ecatDevice);

    if (deviceModules) {
      // Iterate modules in reverse to prioritize the most recently added ones,
      // since newer modules (like SMM LW2) tend to contain more complete definitions.
      for (let i = deviceModules.length - 1; i >= 0; i--) {
        const module = deviceModules[i];

        // Try to find the object in the module's dictionary.
        object = module.profile?.dictionary?.objects?.findByIndex(parameter.index);

        if (object) {
          // If found, update the active dictionary reference to the module's.
          dictionary = module.profile.dictionary;

          // Identify if the object belongs to a known SMM module based on moduleIdent.
          const moduleIdent = module.type?.moduleIdent?.toLowerCase();
          if (['#x22d20001', '#x22d20002', '#x22d20003', '#x22d20004'].includes(moduleIdent)) {
            out.isSmm = true;
          }

          break; // Exit early once the object is found.
        }
      }
    }
  }

  // If no object was found in either the device or its modules dictionaries there is nothing to
  // extend, so fall back to permissive metadata and report the missing object definition.
  if (!object) {
    const parameterId = makeParameterId(parameter);

    return {
      ...out,
      ...getParameterEsiExtensionFallback(parameter),
      esiExtensionError: new Error(
        `No object ${parameterId} found in ESI. Most likely the ESI file is missing this object definition.`
      ),
    };
  }

  // Check if the object is of a variable data type
  if (dictionary.isObjectOfDataTypeVariable(object)) {
    // Determine if the parameter is mandatory by checking the 'category' flag ('m' = mandatory)
    out.mandatory = dictionary.adoptedObjectFlag(object, 'category') === 'm';

    // For non-floating-point types, retrieve min and max allowed values for validation
    if (!dictionary.isObjectTypeFloating(object)) {
      // Skip min/max validation for REAL (floating) types due to complexity or unsupported constraints
      out.min = dictionary.minAllowedValueForObject(object);
      out.max = dictionary.maxAllowedValueForObject(object);
    }

    // Get the default value of the object, converted to a number if possible
    out.defaultData = dictionary.defaultDataToNumberForObject(object);

    // Extract unit symbol if available; fallback to empty string if none provided
    out.unit = object.info.unit ? object.info.unitSymbol : '';

    // Store the bit size of the object (parameter size in bits)
    out.bitSize = object.bitSize;

    // Handle STRING type parameters by extracting the defined max length for validation
    const strTypeRegex = /STRING\((\d+)\)/;
    const strTypeRegexMatch = object.type.match(strTypeRegex);
    if (strTypeRegexMatch) {
      // The firmware encounters issues when STRING(50) is used, and a value of 48 or more characters is assigned.
      // Firmware will fail to load the value, resulting in an empty string. With 47 characters it works.
      const strlen = parseInt(strTypeRegexMatch[1], 10) - 3;
      out.strlen = strlen < 1 ? 1 : strlen;
    }

    // Store the exact ESI data type string (e.g., "UINT16", "STRING(50)", etc.)
    out.esiType = object.type;

    // Fetch a human-readable description for the parameter
    out.description = dictionary.getDescriptionForObject(object);

    // Attempt to retrieve options for the parameter, if any, and parse them from JSON-like string
    const options = dictionary.getOptionsForObject(object);
    if (options) {
      // Clean up line breaks before parsing to avoid JSON parse errors
      out.options = JSON.parse(options.replace(/\r?\n|\r/g, ''));
    }

    // Determine if parameter can be mapped as a RxPDO based on the 'pdoMapping' flag
    out.canBeMappedAsRxPdo = ['r', 'tr'].includes(dictionary.adoptedObjectFlag(object, 'pdoMapping'));

    // Determine if parameter can be mapped as a TxPDO based on the 'pdoMapping' flag
    out.canBeMappedAsTxPdo = ['t', 'tr'].includes(dictionary.adoptedObjectFlag(object, 'pdoMapping'));
  } else {
    // Assign the group name from the object's nameValue property
    out.group = object.nameValue;

    // Find the specific sub-item of the object matching the device parameter's subindex
    const subItem = dictionary.findObjectSubItemBySubIndex(object, parameter.subindex);

    if (!subItem) {
      const parameterId = makeParameterId(parameter);
      const message = `No subitem found for object ${parameterId} in ESI. Most likely the ESI file is missing this subitem definition.`;

      console.warn(`${message} Object name: ${object.nameValue}.`);

      // Without a sub-item definition there is no type or constraint information to read,
      // so fall back to permissive metadata and report the missing sub-item definition.
      Object.assign(out, getParameterEsiExtensionFallback(parameter));

      out.esiExtensionError = new Error(message);
    } else {
      // Get the data type information for this sub-item
      const subItemType = dictionary.subItemTypeForObjectSubItem(object, subItem);

      // Determine if this sub-item is mandatory by checking its 'category' flag ('m' means mandatory)
      out.mandatory = dictionary.adoptedObjectSubItemFlag(object, subItem, 'category') === 'm';

      // For non-floating sub-item types, retrieve the minimum and maximum allowed values for validation
      // Floating-point types (REAL) skip min/max validation due to complexity or lack of constraints
      if (!dictionary.isObjectSubItemTypeFloating(object, subItem)) {
        out.min = dictionary.minAllowedValueForObjectSubItem(object, subItem);
        out.max = dictionary.maxAllowedValueForObjectSubItem(object, subItem);
      }

      // Extract the default value for the sub-item, converted to a number if possible
      out.defaultData = dictionary.defaultDataToNumberForObjectSubItem(object, subItem);

      // Use unit symbol from the sub-item's info if available; fallback to empty string otherwise
      out.unit = subItem.info.unit ? subItem.info.unitSymbol : '';

      // Store the bit size and ESI data type string of the sub-item for further use
      out.bitSize = subItemType.bitSize;
      out.esiType = dictionary.baseTypeForObjectSubItem(object, subItem);

      // Retrieve the general description for the parent object
      const objectDescription = dictionary.getDescriptionForObject(object);

      // Handle description and options differently depending on subindex and data type
      if (parameter.subindex === 0) {
        // For subindex 0, use the object description directly
        out.description = objectDescription;
      } else {
        if (dictionary.isObjectOfDataTypeArray(object)) {
          // For array data types, use the object description and attempt to parse options
          out.description = objectDescription;
          const options = dictionary.getOptionsForObject(object);
          if (options) {
            // Clean up newlines before parsing JSON options
            out.options = JSON.parse(options.replace(/\r?\n|\r/g, ''));
          }
        } else {
          // For record data types, store the object description as recordDescription
          if (dictionary.isObjectOfDataTypeRecord(object)) {
            out.recordDescription = objectDescription;
          }

          // For sub-items, get a more specific description and options
          out.description = dictionary.getDescriptionForObjectSubItem(object, subItem);
          const options = dictionary.getOptionsForObjectSubItem(object, subItem);
          if (options) {
            out.options = JSON.parse(options.replace(/\r?\n|\r/g, ''));
          }
        }
      }

      // Determine if the sub-item can be mapped as a RxPDO based on its 'pdoMapping' flag
      out.canBeMappedAsRxPdo = ['r', 'tr'].includes(dictionary.adoptedObjectSubItemFlag(object, subItem, 'pdoMapping'));

      // Determine if the sub-item can be mapped as a TxPDO based on its 'pdoMapping' flag
      out.canBeMappedAsTxPdo = ['t', 'tr'].includes(dictionary.adoptedObjectSubItemFlag(object, subItem, 'pdoMapping'));
    }
  }

  // Correct the case where the dictionary returns the string 'undefined'
  // instead of an actual undefined value for the description.
  if (out.description === 'undefined') {
    out.description = undefined;
  }

  // Assign typeValueKey and typeValue from parameter if typeValueKey is present.
  // This prioritizes typeValueKey over typeValue for consistency.
  if (parameter.typeValueKey) {
    out.typeValueKey = parameter.typeValueKey;
    out.typeValue = parameter.typeValueKey;
  } else if (parameter.typeValue) {
    // Otherwise, fallback to using typeValue if available.
    out.typeValue = parameter.typeValue;
  }

  if (out.esiType?.startsWith('STRING') || out.esiType?.startsWith('ARRAY')) {
    out.inputType = 'text';
  } else if (out.esiType) {
    out.inputType = 'number';
  } else {
    // The ESI data type is unknown, keep the input type inferred from the parameter type instead.
    out.inputType ??= getParameterEsiExtensionFallback(parameter).inputType;
  }

  // If the parameter has predefined options, override input type to a select dropdown.
  if (out.options) {
    out.inputType = 'select';
  }

  // Support IPv4 input type for ethernet fieldbus:
  // 1. IP address
  // 2. Network mask
  // 3. Gateway
  // 4. Name server
  // 5. Name server 2
  if (parameter.index === 0x2e00 && [1, 2, 3, 4, 5].includes(parameter.subindex)) {
    out.inputType = 'ipv4';
  }

  // Preserve the original unit and options for later reference or restoration.
  out.originalUnit = out.unit;
  out.originalOptions = out.options;

  return out;
}
